<template>
  <div
    :class="containerClasses"
    :data-testid="containerTestId"
    style="display: block"
  >
    <PeriodPickerHeader
      :header-text="headerText"
      :has-next-page="hasNextPage"
      :has-previous-page="hasPreviousPage"
      :new-layout="newLayout"
      @show-next-page="showNextPage"
      @show-previous-page="showPreviousPage"
      @header-click="handleHeaderClick"
    />
    <div
      v-for="(row, row_index) in yearsInRows"
      :key="`year-row-${row_index}`"
      :class="rowClasses"
      :data-testid="rowTestId"
    >
      <span
        v-for="(year, year_index) in row"
        :key="`year-${year_index}`"
        :class="yearClasses(year)"
        :title="String(year.label)"
        @click="handleYearClick(year)"
      >
        {{ year.label }}
        <i
          v-show="hasPrivatePeriods(year) && hasPeriodsWithData(year)"
          :class="privateIconClasses(year)"
        />
      </span>
    </div>
  </div>
</template>

<script>
import chunk from 'lodash/chunk'
import classNames from 'classnames'
import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'

import PeriodPickerHeader from './period-picker-header'
import PeriodPickerMixin from './period-picker-mixin'

const PeriodPickerYears = {
  components: {
    PeriodPickerHeader,
  },
  mixins: [PeriodPickerMixin],
  props: {
    years: Array,
    highlightedYear: Object,
    newLayout: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['year-click', 'header-click'],
  data() {
    const pageSize = this.newLayout ? 12 : 10
    const pages = chunk(this.years, pageSize)
    // findIndex returns -1 when no year is highlighted (e.g. the active ledger
    // is archived/invalid). -1 is truthy, so `|| 0` would not catch it and the
    // pagination would index an out-of-range page. Fall back to the first page.
    const matchingPage = pages.findIndex((yearsOnPage) =>
      includes(yearsOnPage, this.highlightedYear)
    )
    const currentPage = matchingPage === -1 ? 0 : matchingPage

    return {
      currentPage,
    }
  },
  computed: {
    pageSize() {
      return this.newLayout ? 12 : 10
    },
    rowSize() {
      return this.newLayout ? 4 : 2
    },
    currentPageData() {
      return this.pages[this.currentPage]
    },
    firstYearOnPage() {
      return this.currentPageData[0]
    },
    hasPreviousPage() {
      return this.currentPage > 0
    },
    hasNextPage() {
      return this.currentPage < this.pages.length - 1
    },
    headerText() {
      return `${this.firstYearOnPage.label}-${this.lastYearOnPage.label}`
    },
    lastYearOnPage() {
      return this.currentPageData[this.currentPageData.length - 1]
    },
    nextYearClass() {
      return classNames({
        'not-visible': !this.hasNextPage,
      })
    },
    pages() {
      return chunk(this.years, this.pageSize)
    },
    previousYearClass() {
      return classNames({
        'not-visible': !this.hasPreviousPage,
      })
    },
    yearsInRows() {
      return chunk(this.currentPageData, this.rowSize)
    },
    // New-layout specs target the picker via `data-testid` instead of the
    // legacy `.periodpicker-years` structural classes.
    containerTestId() {
      return this.newLayout ? 'periodpicker-years' : null
    },
    rowTestId() {
      return this.newLayout ? 'periodpicker-years-row' : null
    },
    containerClasses() {
      if (this.newLayout) {
        return ['tw-block']
      }
      return ['periodpicker-years', 'tw-block']
    },
    rowClasses() {
      if (this.newLayout) {
        return ['tw-grid', 'tw-grid-cols-4', 'tw-w-full']
      }
      return ['periodpicker-years-row']
    },
  },
  methods: {
    yearClasses(year) {
      const disabled = !this.hasPeriodsWithData(year)
      const isActive = isEqual(year, this.highlightedYear)
      const isPrivate = this.hasPrivatePeriods(year) && !disabled

      if (this.newLayout) {
        return [
          'tw-relative',
          'tw-h-10',
          'tw-leading-10',
          'tw-m-1',
          'tw-text-center',
          'tw-rounded-md',
          'tw-text-base',
          {
            'tw-bg-sf-primary tw-text-white tw-cursor-pointer': isActive,
            'tw-text-sf-slate-dark-1 hover:tw-bg-sf-slate-subtle-2 tw-cursor-pointer':
              !isActive && !disabled,
            'tw-text-sf-slate-dark-1/40 tw-cursor-default': disabled,
          },
        ]
      }

      return classNames({
        'period-picker-item': true,
        year: true,
        private: isPrivate,
        active: isActive,
        disabled,
      })
    },
    privateIconClasses(year) {
      if (this.newLayout) {
        const isActive = isEqual(year, this.highlightedYear)
        return [
          'fa',
          'fa-eye-slash',
          'tw-absolute',
          'tw-bottom-1',
          'tw-right-1',
          'tw-text-xs',
          isActive ? 'tw-text-white/70' : 'tw-text-sf-slate-light-1',
        ]
      }
      return ['fa', 'fa-eye-slash']
    },
    showPreviousPage() {
      if (this.hasPreviousPage) {
        this.currentPage = this.currentPage - 1
      }
    },
    showNextPage() {
      if (this.hasNextPage) {
        this.currentPage = this.currentPage + 1
      }
    },
    handleYearClick(year) {
      if (this.hasPeriodsWithData(year)) {
        this.$emit('year-click', { year: year })
      }
    },
    handleHeaderClick() {
      this.$emit('header-click')
    },
  },
}

export default PeriodPickerYears
</script>
